import React, { useMemo } from 'react'
import { Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

/**
 * 多關鍵字 tag 搜尋輸入框
 *
 * 取代既有單一 keyword 搜尋的 <Input />，使用者輸入關鍵字後按 Enter / 逗號 / 空白
 * 即可形成 tag。最多 5 組，使用 OR 邏輯送往後端（後端走 SearchableConcern）。
 *
 * Props:
 *   value     string[]  目前的關鍵字陣列（外層 state 應改為 array）
 *   onChange  (string[]) => void
 *   placeholder  string
 *   maxCount  number    預設 5
 *   size      'small' | 'middle' | 'large'
 *   style     React.CSSProperties
 *   disabled  boolean
 */
export default function KeywordSearchInput({
  value,
  onChange,
  placeholder,
  maxCount = 5,
  size = 'middle',
  style,
  disabled = false,
  ...rest
}) {
  const { t } = useTranslation()

  const safeValue = useMemo(() => Array.isArray(value) ? value : [], [value])

  const handleChange = (next) => {
    const trimmed = (next || [])
      .map(v => (typeof v === 'string' ? v.trim() : v))
      .filter(v => v != null && v !== '')
    const deduped = Array.from(new Set(trimmed)).slice(0, maxCount)
    onChange?.(deduped)
  }

  const reachedMax = safeValue.length >= maxCount
  const resolvedPlaceholder = reachedMax
    ? t('common.label.keyword_search_max_reached', { count: maxCount })
    : (placeholder || t('common.label.keyword_search_placeholder', { count: maxCount }))

  return (
    <Select
      mode="tags"
      value={safeValue}
      onChange={handleChange}
      open={false}
      maxCount={maxCount}
      tokenSeparators={[',', ' ', '\n', '\t']}
      placeholder={resolvedPlaceholder}
      suffixIcon={<SearchOutlined />}
      maxTagCount="responsive"
      allowClear
      size={size}
      style={{ width: '100%', ...style }}
      disabled={disabled}
      notFoundContent={null}
      {...rest}
    />
  )
}
